import AddIcon from "@mui/icons-material/Add";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
  Box,
  Divider,
  IconButton,
  Popover,
  Stack,
  TextField,
} from "@mui/material";
import { logger } from "@platform/browser-logger";
import { Button, Typography } from "@platform/design-system";
import { CustomCellEditorProps } from "ag-grid-react";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import { BRAND_PURPLE } from "./editorColors";

import type { AgeReductionScheduleItem } from "../../types/values.types";

export interface PopoverListField {
  key: string;
  label: string;
  type?: "number" | "text" | "percentage" | "currency";
  min?: number;
  max?: number;
  placeholder?: string;
}

export interface PopoverListEditorParams {
  fields: PopoverListField[];
  minEntries?: number;
  maxEntries?: number;
  title?: string;
  inputFormat?: string; // DSL format for input masking (e.g., "XX% at XX" or "XX - XX $XX,XXX")
  /** If true, uses two separate number inputs for age reduction schedule (percent and age) */
  ageReductionMode?: boolean;
  disableCloseOnBlur?: boolean;
}

export interface PopoverListEditorRef {
  getValue: () => Record<string, unknown>[];
}

/**
 * Parse input string based on format template
 * Examples:
 * - Format: "XX% at XX", Input: "50% at 65" -> {reductionPercent: 50, age: 65}
 * - Format: "XX - XX $XX,XXX", Input: "35 - 45 $50,000" -> {ageLower: 35, ageUpper: 45, amount: 50000}
 */
const parseInputFormat = (
  input: string,
  format: string,
  fields: PopoverListField[],
): Record<string, unknown> | null => {
  if (!input || !format || !fields.length) return null;

  const result: Record<string, unknown> = {};

  // Extract tokens from format in order: $XX,XXX, XX%, or XX
  const tokenPattern = /(\$XX,XXX|XX%|XX)/g;
  const tokens = format.match(tokenPattern) || [];

  if (tokens.length !== fields.length) {
    logger.warn("Format tokens count does not match fields count", {
      tokens,
      fields,
    });
    return null;
  }

  // Clean and extract numbers from input
  const numberPattern = /[\d,]+(?:\.\d+)?/g;
  const numbers = input.match(numberPattern) || [];

  if (numbers.length !== tokens.length) {
    return null; // Not enough values parsed
  }

  // Map each number to its corresponding field
  fields.forEach((field, index) => {
    const rawValue = numbers[index];
    if (!rawValue) {
      result[field.key] = "";
      return;
    }

    // Remove commas and parse
    const cleanedValue = rawValue.replace(/,/g, "");
    const numValue = parseNumericFieldValue(cleanedValue, field);

    if (isNaN(numValue)) {
      result[field.key] = "";
    } else {
      result[field.key] = numValue;
    }
  });

  return result;
};

/**
 * Format entry values into display string based on format template
 */
const formatEntryDisplay = (
  entry: Record<string, unknown>,
  format: string,
  fields: PopoverListField[],
): string => {
  if (!format) return "";

  let result = format;

  // Extract tokens from format in the order they appear
  const tokenPattern = /(\$XX,XXX|XX%|XX)/g;
  const tokens: string[] = [];
  let match;
  while ((match = tokenPattern.exec(format)) !== null) {
    tokens.push(match[0]);
  }

  // Replace each token with its corresponding field value in order
  fields.forEach((field, index) => {
    if (index >= tokens.length) return;

    const value = entry[field.key];
    if (value === undefined || value === null || value === "") return;

    const num =
      typeof value === "string" ? parseFloat(value) : (value as number);
    if (isNaN(num)) return;

    const token = tokens[index];
    let formatted: string;

    if (token === "$XX,XXX") {
      formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(num);
    } else if (token === "XX%") {
      formatted = `${num}%`;
    } else {
      // "XX"
      formatted = String(num);
    }

    // Replace the first occurrence of this specific token
    result = result.replace(token, formatted);
  });

  return result;
};

/**
 * Gets numeric field value based on min/max constraints
 * @param fieldValue - The raw value from the input (string or number)
 * @param field - The field configuration with min/max constraints
 * @returns The validated and clamped numeric value, or NaN if invalid
 */
const parseNumericFieldValue = (
  fieldValue: string,
  field: PopoverListField,
): number => {
  const numValue = parseFloat(fieldValue);
  if (isNaN(numValue)) {
    return NaN;
  }

  let validatedValue = numValue;

  if (field.min !== undefined && numValue < field.min) {
    validatedValue = field.min;
  }

  if (field.max !== undefined && numValue > field.max) {
    validatedValue = field.max;
  }

  return validatedValue;
};

/**
 * Generic popover list editor for AG Grid
 * Allows users to add/remove/edit entries in a list with configurable fields
 */
interface EditableWhenColDef {
  editableWhen?: { dependsOn: string; disabledValues?: unknown[] };
}

const computeIsDisabled = (
  editableWhen: EditableWhenColDef["editableWhen"],
  nodeData: Record<string, unknown> | undefined,
): boolean => {
  if (!editableWhen || !nodeData) return false;
  const disabledValues = editableWhen.disabledValues || [];
  const dependsOnValue = nodeData[editableWhen.dependsOn];
  return disabledValues.includes(dependsOnValue);
};

const getEntryCountHelperText = (
  minEntries: number | undefined,
  maxEntries: number | undefined,
): string => {
  if (minEntries && maxEntries) {
    return `Enter ${minEntries} to ${maxEntries} options`;
  }
  if (minEntries) {
    return `Enter at least ${minEntries} options`;
  }
  return `Enter up to ${maxEntries} options`;
};

const parseInitialEntries = (
  rawValue: string | Record<string, unknown>[] | null | undefined,
): Record<string, unknown>[] => {
  if (Array.isArray(rawValue)) return rawValue;
  if (typeof rawValue === "string" && rawValue.length) {
    return JSON.parse(rawValue) as Record<string, unknown>[];
  }
  return [];
};

interface ResolvedEditorParams {
  fields: PopoverListField[];
  minEntries: number | undefined;
  maxEntries: number | undefined;
  title: string;
  inputFormat: string | undefined;
  ageReductionMode: boolean;
  disableCloseOnBlur: boolean;
}

const resolveEditorParams = (
  props: CustomCellEditorProps<
    Record<string, unknown>,
    string | Record<string, unknown>[],
    unknown
  >,
): ResolvedEditorParams => {
  // AG Grid passes cellEditorParams through the params object when using cellEditorSelector
  const editorParams =
    (props as unknown as { cellEditorParams?: unknown }).cellEditorParams ??
    props;
  const params = editorParams as PopoverListEditorParams;
  return {
    fields: params?.fields || [],
    minEntries: params?.minEntries,
    maxEntries: params?.maxEntries,
    title: params?.title || "Configure Entries",
    inputFormat: params?.inputFormat,
    ageReductionMode: params?.ageReductionMode || false,
    disableCloseOnBlur: params?.disableCloseOnBlur || false,
  };
};

export const PopoverListEditor = forwardRef<
  PopoverListEditorRef,
  CustomCellEditorProps<
    Record<string, unknown>,
    string | Record<string, unknown>[],
    unknown
  >
>((props, ref) => {
  const { stopEditing, api, node, column } = props;
  const value = parseInitialEntries(props.value);

  const {
    fields,
    minEntries,
    maxEntries,
    title,
    inputFormat,
    ageReductionMode,
    disableCloseOnBlur,
  } = resolveEditorParams(props);

  // Check if field is disabled based on editableWhen conditions
  const colDef = column?.getColDef() as unknown as
    | EditableWhenColDef
    | undefined;
  const isDisabled = computeIsDisabled(
    colDef?.editableWhen,
    node?.data as Record<string, unknown> | undefined,
  );

  // If disabled, immediately stop editing and don't open popover
  useEffect(() => {
    if (isDisabled && stopEditing) {
      stopEditing();
    }
  }, [isDisabled, stopEditing]);

  // Helper function to sort entries by age (for age reduction mode)
  const sortEntriesByAge = (
    entriesToSort: Record<string, unknown>[],
  ): Record<string, unknown>[] => {
    if (!ageReductionMode) return entriesToSort;
    return [...entriesToSort].sort((a, b) => {
      const ageA = typeof a["age"] === "number" ? a["age"] : 0;
      const ageB = typeof b["age"] === "number" ? b["age"] : 0;
      return ageA - ageB;
    });
  };

  const renderAgeReductionEntry = (
    entry: Record<string, unknown>,
    index: number,
  ): React.JSX.Element => {
    const entryItem = entry as unknown as AgeReductionScheduleItem;
    const percentValue = entryItem.reductionPercent || "";
    const ageValue = entryItem.age || "";

    return (
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flex: 1 }}>
        <TextField
          type="number"
          value={percentValue}
          onChange={(e) => {
            e.stopPropagation();
            handleFieldChange(index, "reductionPercent", e.target.value);
          }}
          onFocus={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleEnterKey}
          size="small"
          placeholder="%"
          inputProps={{
            min: 1,
            max: 100,
            step: 1,
          }}
          sx={{ width: "80px" }}
          autoComplete="off"
        />
        <Typography variant="body-medium" sx={{ flexShrink: 0 }}>
          % at
        </Typography>
        <TextField
          type="number"
          value={ageValue}
          onChange={(e) => {
            e.stopPropagation();
            handleFieldChange(index, "age", e.target.value);
          }}
          onFocus={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleEnterKey}
          size="small"
          placeholder="age"
          inputProps={{
            min: 1,
            max: 120,
            step: 1,
          }}
          sx={{ width: "80px" }}
          autoComplete="off"
        />
      </Box>
    );
  };

  // Initialize entries from value or create empty array (sorted if age reduction mode)
  const initialEntries = sortEntriesByAge(value);
  const [entries, setEntries] =
    useState<Record<string, unknown>[]>(initialEntries);

  // Local state for masked input fields (when inputFormat is provided)
  const [inputValues, setInputValues] = useState<string[]>(
    initialEntries.map((entry) =>
      inputFormat ? formatEntryDisplay(entry, inputFormat, fields) : "",
    ),
  );

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Expose getValue method to AG Grid
  useImperativeHandle(ref, () => ({
    getValue: () => entries,
  }));

  // Auto-open popover when editor is created (only if not disabled)
  useEffect(() => {
    if (!isDisabled) {
      const timer = setTimeout(() => {
        setOpen(true);
      }, 10);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isDisabled]);

  const handleClose = (): void => {
    setOpen(false);
    // Stop editing and apply the value when popover closes
    if (stopEditing) {
      setTimeout(() => {
        stopEditing();
      }, 0);
    }
  };

  const handleDone = (): void => {
    // Set the data value directly on the node using the column field name
    if (node && column) {
      const columnField = column.getColId();
      node.setDataValue(columnField, entries);
    }

    // Force AG Grid to accept the current value and refresh the cell
    if (stopEditing) {
      stopEditing();
    }

    // Refresh the cell to update the display
    // Example: refreshCells([{ rowId: node.id, field: column.getColId() }]);
    if (api && node) {
      setTimeout(() => {
        api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    }

    setOpen(false);
  };

  const handleAddEntry = (): void => {
    if (maxEntries && entries.length >= maxEntries) {
      return;
    }

    const newEntry: Record<string, unknown> = {};
    fields.forEach((field) => {
      newEntry[field.key] = "";
    });

    const updatedEntries = sortEntriesByAge([...entries, newEntry]);
    setEntries(updatedEntries);
    setInputValues([...inputValues, ""]);
  };

  const handleRemoveEntry = (index: number): void => {
    const updatedEntries = sortEntriesByAge(
      entries.filter((_, i) => i !== index),
    );
    setEntries(updatedEntries);
    setInputValues(inputValues.filter((_, i) => i !== index));
  };

  const handleMaskedInputChange = (index: number, value: string): void => {
    // Parse the input immediately
    if (inputFormat) {
      const parsed = parseInputFormat(value, inputFormat, fields);

      if (parsed) {
        // Update entries with parsed values
        setEntries((prevEntries) => {
          const updated = [...prevEntries];
          updated[index] = parsed;
          return updated;
        });

        // Format and display the result
        const formatted = formatEntryDisplay(parsed, inputFormat, fields);
        setInputValues((prev) => {
          const updated = [...prev];
          updated[index] = formatted;
          return updated;
        });
      } else {
        // Update entries with empty object if parsing fails
        setEntries((prevEntries) => {
          const updated = [...prevEntries];
          updated[index] = {};
          return updated;
        });

        // If parsing fails, just update the raw input for now
        setInputValues((prev) => {
          const updated = [...prev];
          updated[index] = value;
          return updated;
        });
      }
    }
  };

  const handleFieldChange = (
    index: number,
    fieldKey: string,
    fieldValue: string,
  ): void => {
    const updatedEntries = [...entries];

    // Convert the value based on field type
    let processedValue: string | number = fieldValue;

    const field = fields.find((f) => f.key === fieldKey);
    if (
      field &&
      (field.type === "number" ||
        field.type === "percentage" ||
        field.type === "currency")
    ) {
      const numValue = parseNumericFieldValue(fieldValue, field);

      if (!isNaN(numValue)) {
        processedValue = numValue;
      }
    }

    updatedEntries[index] = {
      ...updatedEntries[index],
      [fieldKey]: processedValue,
    };
    const sortedEntries = sortEntriesByAge(updatedEntries);
    setEntries(sortedEntries);
  };

  // Check if all entries have valid values (non-empty fields)
  const isEntryValid = (entry: Record<string, unknown>): boolean =>
    fields.every((field) => {
      const value = entry[field.key];
      if (value === undefined || value === null || value === "") return false;
      if (
        field.type === "number" ||
        field.type === "percentage" ||
        field.type === "currency"
      ) {
        const num =
          typeof value === "string" ? parseFloat(value) : (value as number);
        return !isNaN(num);
      }
      return true;
    });

  const hasInvalidEntries = entries.some((entry) => !isEntryValid(entry));
  const validEntryCount = entries.filter(isEntryValid).length;
  const belowMinEntries =
    minEntries !== undefined && validEntryCount < minEntries;
  const isDoneDisabled =
    belowMinEntries || (hasInvalidEntries && entries.length > 0);

  const handleEnterKey = (event: ReactKeyboardEvent): void => {
    event.stopPropagation();
    if (event.key === "Enter" && !isDoneDisabled) {
      event.preventDefault();
      handleDone();
    }
  };

  const renderField = (
    entry: Record<string, unknown>,
    index: number,
    field: PopoverListField,
  ): React.JSX.Element => {
    const value = (entry[field.key] as string | number | undefined) ?? "";

    let inputProps: Record<string, unknown> = {};
    if (
      field.type === "number" ||
      field.type === "percentage" ||
      field.type === "currency"
    ) {
      inputProps = {
        type: "number",
        inputProps: {
          min: field.min,
          max: field.max,
          step: field.type === "percentage" ? 1 : "any",
        },
      };
    }

    return (
      <TextField
        key={field.key}
        label={field.label}
        value={value}
        onChange={(e) => {
          e.stopPropagation();
          handleFieldChange(index, field.key, e.target.value);
        }}
        onFocus={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleEnterKey}
        size="small"
        fullWidth
        placeholder={field.placeholder}
        autoComplete="off"
        {...inputProps}
      />
    );
  };

  const renderEntryFields = (
    entry: Record<string, unknown>,
    index: number,
  ): React.JSX.Element => {
    if (ageReductionMode) {
      // Age reduction mode - two number inputs for percent and age
      return renderAgeReductionEntry(entry, index);
    }
    if (inputFormat) {
      // Masked input mode - single field with format parsing
      return (
        <TextField
          label={title}
          value={inputValues[index] || ""}
          onChange={(e) => {
            e.stopPropagation();
            handleMaskedInputChange(index, e.target.value);
          }}
          onFocus={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleEnterKey}
          size="small"
          fullWidth
          placeholder={inputFormat}
          autoComplete="off"
          helperText={`Format: ${inputFormat}`}
        />
      );
    }
    // Individual fields mode
    return (
      <Stack spacing={1} sx={{ flex: 1 }}>
        {fields.map((field) => renderField(entry, index, field))}
      </Stack>
    );
  };

  // Don't render popover if disabled
  if (isDisabled) {
    return <div ref={anchorRef} style={{ width: "100%", height: "100%" }} />;
  }

  return (
    <>
      <div ref={anchorRef} style={{ width: "100%", height: "100%" }} />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={disableCloseOnBlur ? () => {} : handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableEnforceFocus
        disableAutoFocus={false}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              minWidth: 400,
              maxWidth: 600,
              maxHeight: 600,
              overflow: "auto",
            },
          },
        }}
      >
        <Box
          sx={{ p: 2 }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleEnterKey}
        >
          {entries.length === 0 ? (
            <Typography
              variant="body-medium"
              color="textSecondary"
              sx={{ mb: 2 }}
            >
              No entries configured. Click &quot;Add Option&quot; to get
              started.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ mb: 2 }}>
              {entries.map((entry, index) => (
                // eslint-disable-next-line react/no-array-index-key -- entries are plain unkeyed objects re-sorted by age; no stable id exists to key on
                <Box key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "flex-start",
                    }}
                  >
                    {renderEntryFields(entry, index)}
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveEntry(index)}
                      sx={{ mt: 0.5 }}
                    >
                      <CloseOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {index < entries.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))}
            </Stack>
          )}

          <Button
            variant="secondary"
            startIcon={<AddIcon />}
            onClick={handleAddEntry}
            fullWidth
            disabled={maxEntries ? entries.length >= maxEntries : false}
            sx={{
              borderStyle: "dashed",
              borderColor: BRAND_PURPLE,
              color: BRAND_PURPLE,
            }}
          >
            Add Option
          </Button>

          {!belowMinEntries && (minEntries || maxEntries) && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: "block", mt: 1 }}
            >
              {getEntryCountHelperText(minEntries, maxEntries)}
            </Typography>
          )}

          {belowMinEntries && (
            <Typography
              variant="caption"
              color="danger"
              sx={{ display: "block", mt: 1 }}
            >
              At least {minEntries} valid options required
            </Typography>
          )}

          {hasInvalidEntries && entries.length > 0 && !belowMinEntries && (
            <Typography
              variant="caption"
              color="danger"
              sx={{ display: "block", mt: 1 }}
            >
              All fields must have a valid value
            </Typography>
          )}

          <Box
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button
              onClick={handleDone}
              variant="primary"
              disabled={
                belowMinEntries || (hasInvalidEntries && entries.length > 0)
              }
            >
              Done
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
});

PopoverListEditor.displayName = "PopoverListEditor";
